import * as v from "valibot";

type ApiResponse = { Result: string; Type: string; Url: string }[] | null;

// Remove any non-alphanumeric characters from the search term.
// We only want to send search queries to Elasticsearch that have at least two
// alphanumeric characters.
const scrubbed = new RegExp(/[\W]/g);

const SearchQuerySchema = v.pipe(
    v.string(),
    v.transform((input) => input.replaceAll(scrubbed, "")),
    v.trim(),
    v.minLength(MIN_SEARCH_LENGTH)
);

export const useAutocomplete = (apiUrl: string = "partautocomplete") => {
    const { $api } = useNuxtApp();
    let controller: AbortController | null = null;

    function isQueryValid(query?: string) {
        if (!query || query.length < MIN_SEARCH_LENGTH) return false; // quick check
        const result = v.safeParse(SearchQuerySchema, query);
        return result.success;
    }

    async function autocomplete(
        searchText: string,
        parameters?: Record<string, string | number | undefined>
    ): Promise<AutocompleteItem[]> {
        const query = searchText.trim();
        if (!isQueryValid(query)) return [];

        if (controller) controller.abort(); // abort in-progress request, if any
        controller = new AbortController();

        try {
            const response = await $api<ApiResponse>(apiUrl, {
                method: "POST",
                query: parameters,
                body: { query },
                signal: controller.signal,
                onResponse() {
                    controller = null;
                },
            });
            return Array.isArray(response)
                ? response.map((item) => ({
                      label: item.Result,
                      category: item.Type,
                      url: item.Url,
                  }))
                : [];
        } catch (_err) {
            // aborted or server issue, no need to bother the UI
        }

        return [];
    }

    return { autocomplete, isQueryValid };
};
